/* ==========================================================================
   ThrottleCo Verified — tb-verified.js (theme v3.1)
   Forms (rate / photo / fitment / vote), votes, marketing opt-in, lightbox, video seeking,
   chip filters, garage pinning. Everything renders server-side; this file only
   adds interaction and posts to the Apps Script endpoint in window.tbVerified.
   ========================================================================== */
(function () {
  'use strict';
  var cfg = window.tbVerified || {};
  var MODALS = { rate: 'tb-modal-rate', photo: 'tb-modal-photo', fitment: 'tb-modal-fitment', vote: 'tb-modal-vote' };

  var LS = {
    get: function (k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };

  function clientId() {
    var c = LS.get('tb_cid');
    if (!c) {
      c = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      LS.set('tb_cid', c);
    }
    return c;
  }

  // Signed-in Throttle Co customer (rendered by tb-forms.liquid): one-tap votes and prefilled forms.
  function customer() {
    var el = document.querySelector('[data-tb-customer]');
    return { email: (el && el.getAttribute('data-email')) || '', name: (el && el.getAttribute('data-name')) || '' };
  }

  // Best effort: Shopify's own newsletter form creates or updates the customer as subscribed, so Klaviyo sees them.
  function subscribeShopify(email) {
    if (!email) return;
    try {
      var fd = new FormData();
      fd.append('form_type', 'customer'); fd.append('utf8', '\u2713');
      fd.append('contact[email]', email); fd.append('contact[tags]', 'newsletter, tc-verified');
      fetch('/contact', { method: 'POST', body: fd, credentials: 'same-origin', redirect: 'follow' }).catch(function () { /* never block the rider */ });
    } catch (e) { /* ignore */ }
  }

  function garageBike() {
    try {
      if (window.TcGarage && typeof window.TcGarage.get === 'function') return window.TcGarage.get();
      return JSON.parse(localStorage.getItem('tc_garage'));
    } catch (e) { return null; }
  }

  function handleize(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function toast(msg) {
    var el = document.querySelector('[data-tb-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._tbTimer);
    el._tbTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 3400);
  }

  function post(payload) {
    if (!cfg.endpoint) return Promise.reject(new Error('Submissions are not switched on yet. Try again soon.'));
    payload.v = 1;
    payload.token = cfg.token || '';
    payload.client_id = clientId();
    payload.page = location.pathname;
    return fetch(cfg.endpoint, {
      method: 'POST',
      mode: 'cors',
      redirect: 'follow',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    }).then(function (res) { return res.text(); }).then(function (text) {
      try { return JSON.parse(text); } catch (e) { throw new Error('Something went wrong on our end. Try again in a minute.'); }
    });
  }

  /* ---------- Image resize (client side, ≤1600px JPEG) ---------- */
  function loadImg(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { resolve(img); URL.revokeObjectURL(url); };
      img.onerror = function () { reject(new Error('Could not read that photo.')); URL.revokeObjectURL(url); };
      img.src = url;
    });
  }

  function toBase64(blob) {
    return blob.arrayBuffer().then(function (buf) {
      var bytes = new Uint8Array(buf), bin = '', chunk = 0x8000;
      for (var i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
      return btoa(bin);
    });
  }

  function resize(file) {
    if (/heic|heif/i.test(file.type + ' ' + file.name)) {
      return Promise.reject(new Error('HEIC photos are not supported yet. On iPhone, share as JPEG or take a screenshot of it.'));
    }
    if (!/^image\//.test(file.type)) return Promise.reject(new Error('Pick an image file.'));
    var MAX = 1600;
    var p = (window.createImageBitmap) ? createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () { return loadImg(file); }) : loadImg(file);
    return p.then(function (bmp) {
      var w0 = bmp.width || bmp.naturalWidth, h0 = bmp.height || bmp.naturalHeight;
      var scale = Math.min(1, MAX / Math.max(w0, h0));
      var w = Math.max(1, Math.round(w0 * scale)), h = Math.max(1, Math.round(h0 * scale));
      var canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
      if (bmp.close) bmp.close();
      return new Promise(function (resolve) { canvas.toBlob(resolve, 'image/jpeg', 0.82); }).then(function (blob) {
        if (!blob) throw new Error('Could not process that photo.');
        if (blob.size > 1.1 * 1024 * 1024) {
          return new Promise(function (resolve) { canvas.toBlob(resolve, 'image/jpeg', 0.6); });
        }
        return blob;
      });
    }).then(toBase64);
  }

  /* ---------- File input previews ---------- */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;
    var wrap = input.closest('[data-tb-file]');
    if (!wrap) return;
    var files = Array.prototype.slice.call(input.files || [], 0, 4);
    var label = wrap.querySelector('[data-tb-file-label]');
    if (label) label.textContent = files.length ? (files.length + ' photo' + (files.length > 1 ? 's' : '') + ' ready') : label.getAttribute('data-default') || label.textContent;
    var previews = wrap.parentElement.querySelector('[data-tb-file-previews]');
    if (!previews) return;
    previews.innerHTML = '';
    files.forEach(function (f) {
      var img = document.createElement('img');
      img.alt = '';
      img.src = URL.createObjectURL(f);
      img.onload = function () { URL.revokeObjectURL(img.src); };
      previews.appendChild(img);
    });
  });


  /* ---------- Dimension picker (shared rubric) ---------- */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    var scale = input.closest('[data-tb-scale]');
    if (!scale) return;
    if (input.name === 'durability_skip') {
      scale.classList.toggle('is-skipped', input.checked);
      if (input.checked) {
        scale.querySelectorAll('input[type="radio"]').forEach(function (r) { r.checked = false; });
        scale.querySelectorAll('.tb-scale__btn').forEach(function (b) { b.classList.remove('is-below'); });
        var v = scale.querySelector('[data-tb-scale-val]'); if (v) v.textContent = 'skip';
      } else {
        var v2 = scale.querySelector('[data-tb-scale-val]'); if (v2) v2.textContent = '\u2013';
      }
      return;
    }
    if (input.type !== 'radio') return;
    var val = parseInt(input.value, 10);
    var out = scale.querySelector('[data-tb-scale-val]');
    if (out) out.textContent = val;
    scale.querySelectorAll('.tb-scale__btn').forEach(function (b) {
      var bv = parseInt(b.querySelector('input').value, 10);
      b.classList.toggle('is-below', bv < val);
    });
  });

  /* ---------- Open a form ---------- */
  function openForm(type, opener) {
    var id = MODALS[type];
    var modal = id && document.getElementById(id);
    if (!modal) return;
    var handle = (opener && opener.getAttribute('data-product-handle')) || (cfg.product && cfg.product.handle) || '';
    var pid = (opener && opener.getAttribute('data-product-id')) || (cfg.product && cfg.product.id) || '';
    var title = (opener && opener.getAttribute('data-product-title')) || (cfg.product && cfg.product.title) || handle;
    modal.classList.remove('is-done');
    var form = modal.querySelector('[data-tb-form-el]');
    if (form) {
      var h = form.querySelector('[name="product_handle"]'); if (h) h.value = handle;
      var p = form.querySelector('[name="product_id"]'); if (p) p.value = pid;
      var err = form.querySelector('[data-tb-error]'); if (err) err.classList.remove('is-visible');
      var g = garageBike();
      var bikeSel = form.querySelector('select[name="bike"]');
      if (bikeSel && g && g.label && !bikeSel.value) bikeSel.value = g.label;
      var cu = customer();
      var emailIn = form.querySelector('input[name="email"]'); if (emailIn && cu.email && !emailIn.value) emailIn.value = cu.email;
      var nameIn = form.querySelector('input[name="name"]'); if (nameIn && cu.name && !nameIn.value) nameIn.value = cu.name;
    }
    var label = modal.querySelector('[data-tb-form-product]');
    if (label) label.textContent = title;
    if (window.tcModals) window.tcModals.open(id, opener); else modal.classList.add('is-open');
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-tb-open]');
    if (!opener) return;
    event.preventDefault();
    openForm(opener.getAttribute('data-tb-open'), opener);
  });

  /* ---------- Submit ---------- */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-tb-form-el]');
    if (!form) return;
    event.preventDefault();
    var modal = form.closest('[data-tb-form]');
    var action = modal.getAttribute('data-tb-form');
    var err = form.querySelector('[data-tb-error]');
    if (err) { err.classList.remove('is-visible'); err.textContent = ''; }
    if (!form.reportValidity()) return;

    var fd = new FormData(form);
    var data = { action: action };
    fd.forEach(function (v, k) {
      if (k === 'photo' || k === 'photos') return;
      data[k] = typeof v === 'string' ? v.trim() : v;
    });
    data.hp = data.website || ''; delete data.website;
    data.consent = !!fd.get('consent');
    data.consent_photo = !!fd.get('consent_photo');
    data.marketing_opt_in = !!fd.get('marketing_opt_in');
    data.consent_social = !!fd.get('consent_social');
    if (!data.consent_social) delete data.social_handle;
    if (data.stars) data.stars = parseInt(data.stars, 10);
    ['dim_performance', 'dim_durability', 'dim_install', 'dim_build'].forEach(function (k) {
      if (data[k] === undefined || data[k] === '') { delete data[k]; return; }
      data[k] = parseFloat(data[k]);
    });
    if (data.durability_skip) { delete data.dim_durability; }
    delete data.durability_skip;

    var btn = form.querySelector('[type="submit"]');
    var fileInput = form.querySelector('input[type="file"]');
    var files = fileInput && fileInput.files ? Array.prototype.slice.call(fileInput.files, 0, 4) : [];

    function fail(message) {
      if (err) { err.textContent = message; err.classList.add('is-visible'); }
      else toast(message);
    }

    if (fileInput && fileInput.required && !files.length) { fail('Add a photo first.'); return; }

    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }

    var run;
    if (action === 'photo.submit') {
      run = files.reduce(function (chain, f) {
        return chain.then(function (prev) {
          if (prev && !prev.ok) return prev;
          return resize(f).then(function (b64) {
            var payload = Object.assign({}, data, { photo: { b64: b64, mime: 'image/jpeg' } });
            return post(payload);
          });
        });
      }, Promise.resolve(null));
    } else if (files.length) {
      run = resize(files[0]).then(function (b64) { data.photo = { b64: b64, mime: 'image/jpeg' }; return post(data); });
    } else {
      run = post(data);
    }

    run.then(function (result) {
      if (!result || !result.ok) {
        throw new Error((result && result.error && result.error.message) || 'Something went wrong. Try again in a minute.');
      }
      modal.classList.add('is-done');
      var doneText = modal.querySelector('[data-tb-done-text]');
      if (doneText && result.message) doneText.textContent = result.message;
      LS.set('tb_submitted:' + data.product_handle + ':' + action, '1');
      if (action === 'notify.submit') LS.set('tb_notified:' + data.product_handle, '1');
      if (action === 'vote.submit') countVote(data.product_handle);
      if (data.marketing_opt_in) subscribeShopify(data.email);
      toast(action === 'vote.submit' ? 'Vote counted. Most requested parts get tested first.' : 'Posted. Thanks for riding with us.');
    }).catch(function (ex) {
      fail(ex && ex.message ? ex.message : 'Something went wrong.');
    }).then(function () {
      if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
    });
  });

  /* ---------- Votes ---------- */
  function markVoted(handle) {
    document.querySelectorAll('[data-tb-vote][data-product-handle="' + handle + '"]').forEach(function (b) { b.classList.add('is-voted'); });
  }

  function countVote(handle) {
    document.querySelectorAll('[data-tb-vote][data-product-handle="' + handle + '"]').forEach(function (b) {
      b.classList.add('is-voted');
      var c = b.querySelector('[data-tb-vote-count]');
      if (c) c.textContent = (parseInt(c.textContent, 10) || 0) + 1;
    });
    LS.set('tb_voted:' + handle, '1');
    LS.set('tb_notified:' + handle, '1');
  }

  // One vote per rider per product. Signed-in customers vote in one tap; everyone else gives an email in the vote sheet.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-tb-vote]');
    if (!btn) return;
    event.preventDefault();
    var handle = btn.getAttribute('data-product-handle') || '';
    var pid = btn.getAttribute('data-product-id') || '';
    if (LS.get('tb_voted:' + handle)) { toast('Already counted. One vote per rider per product.'); return; }
    var cu = customer();
    if (!cu.email) { openForm('vote', btn); return; }
    var g = garageBike();
    btn.disabled = true;
    post({ action: 'vote.submit', product_handle: handle, product_id: pid, email: cu.email, name: cu.name, bike: (g && g.label) || '' }).then(function (result) {
      if (result && result.ok) { countVote(handle); toast('Vote counted. Most requested parts get tested first.'); return; }
      var code = result && result.error && result.error.code;
      if (code === 'duplicate') { markVoted(handle); LS.set('tb_voted:' + handle, '1'); toast('Already counted. One vote per rider per product.'); return; }
      toast((result && result.error && result.error.message) || 'Could not count that vote. Try again in a minute.');
    }).catch(function () { toast('Could not count that vote. Try again in a minute.'); }).then(function () { btn.disabled = false; });
  });

  /* ---------- Lightbox ---------- */
  document.addEventListener('click', function (event) {
    var a = event.target.closest('[data-tb-lightbox]');
    if (!a) return;
    var modal = document.getElementById('tb-modal-lightbox');
    if (!modal) return;
    event.preventDefault();
    var img = modal.querySelector('[data-tb-lb-img]');
    if (img) { img.src = a.getAttribute('href'); img.alt = a.getAttribute('data-caption') || 'Rider photo'; }
    var cap = modal.querySelector('[data-tb-lb-caption]');
    if (cap) cap.textContent = a.getAttribute('data-caption') || '';
    var meta = modal.querySelector('[data-tb-lb-meta]');
    if (meta) meta.textContent = [a.getAttribute('data-name'), a.getAttribute('data-bike')].filter(Boolean).join(' · ');
    var tier = a.getAttribute('data-tier') || 'rider';
    modal.querySelectorAll('[data-tb-lb-tier]').forEach(function (el) { el.hidden = el.getAttribute('data-tb-lb-tier') !== tier; });
    var link = modal.querySelector('[data-tb-lb-link]');
    if (link) {
      var url = a.getAttribute('data-product-url');
      link.hidden = !url;
      if (url) link.href = url;
    }
    if (window.tcModals) window.tcModals.open('tb-modal-lightbox', a); else modal.classList.add('is-open');
  });

  /* ---------- Video: play + timestamp chips ---------- */
  document.addEventListener('click', function (event) {
    var t = event.target.closest('[data-tb-seek], .tb-video__play');
    if (!t) return;
    var box = t.closest('[data-tb-video]');
    if (!box) {
      var wrap = t.closest('[data-tb-video-wrap]') || document;
      box = wrap.querySelector('[data-tb-video]');
    }
    if (!box) return;
    event.preventDefault();
    var vid = t.getAttribute('data-video-id') || box.getAttribute('data-tb-video');
    if (!vid) return;
    var start = parseInt(t.getAttribute('data-tb-seek') || '0', 10) || 0;
    var iframe = box.querySelector('iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', 'Throttle Bros review video');
      box.appendChild(iframe);
      box.classList.add('is-playing');
    }
    iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(vid) + '?autoplay=1&rel=0&start=' + start;
    var scope = t.closest('[data-tb-video-wrap]') || box.parentElement || document;
    scope.querySelectorAll('[data-tb-seek]').forEach(function (b) { b.classList.toggle('is-active', b === t); });
    if (t.hasAttribute('data-tb-seek')) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  /* ---------- Chip filters ---------- */
  document.addEventListener('click', function (event) {
    var b = event.target.closest('[data-tb-filter]');
    if (!b) return;
    var group = b.closest('[data-tb-filter-group]');
    if (!group) return;
    event.preventDefault();
    group.querySelectorAll('[data-tb-filter]').forEach(function (x) { x.classList.toggle('is-active', x === b); });
    var sel = group.getAttribute('data-tb-filter-target');
    var target = sel ? document.querySelector(sel) : group.nextElementSibling;
    if (!target) return;
    var f = b.getAttribute('data-tb-filter') || 'all';
    var g = garageBike();
    var mine = g && g.label;
    if (f === 'mine' && !mine) {
      toast('Pick your bike in the header first.');
      var pick = document.querySelector('[data-modal-open="tc-garage-modal"]');
      if (pick) pick.click();
    }
    var shown = 0;
    Array.prototype.forEach.call(target.children, function (item) {
      if (!item.hasAttribute('data-bike') && !item.hasAttribute('data-tier')) return;
      var ok = true;
      if (f === 'mine') ok = !!mine && item.getAttribute('data-bike') === mine;
      else if (f === 'verified') ok = item.getAttribute('data-tier') === 'verified_buyer' || item.getAttribute('data-tier') === 'crew';
      else if (f !== 'all') ok = item.getAttribute('data-bike') === f;
      item.classList.toggle('is-hidden', !ok);
      if (ok) shown++;
    });
    var empty = target.querySelector('[data-tb-filter-empty]') || (target.parentElement && target.parentElement.querySelector('[data-tb-filter-empty]'));
    if (empty) empty.hidden = shown > 0;
  });

  /* ---------- Garage: pin the rider's bike ---------- */
  function pinMine() {
    var g = garageBike();
    var label = g && g.label;
    document.querySelectorAll('.tb-fit__row[data-bike]').forEach(function (row) {
      var isMine = !!label && row.getAttribute('data-bike') === label;
      row.classList.toggle('is-mine', isMine);
      if (isMine) {
        var parent = row.parentElement;
        var first = parent && parent.querySelector('.tb-fit__row');
        if (first && first !== row) parent.insertBefore(row, first);
      }
    });
    document.querySelectorAll('.tb-rcard[data-bike], .tb-tile[data-bike]').forEach(function (el) {
      el.classList.toggle('is-mine', !!label && el.getAttribute('data-bike') === label);
    });
    if (label) {
      var slug = 'bike-' + handleize(label);
      document.querySelectorAll('[data-tb-garage-select]').forEach(function (sel) {
        Array.prototype.forEach.call(sel.options, function (o) { if (o.value && o.value.indexOf('/gallery/' + slug) !== -1) sel.value = o.value; });
      });
      document.querySelectorAll('[data-tb-mine-label]').forEach(function (el) { el.textContent = label; });
    }
  }

  document.addEventListener('change', function (event) {
    var sel = event.target.closest('[data-tb-garage-select]');
    if (sel && sel.value && sel.value !== location.pathname) location.href = sel.value;
  });

  function restoreState() {
    document.querySelectorAll('[data-tb-vote][data-product-handle]').forEach(function (b) {
      if (LS.get('tb_voted:' + b.getAttribute('data-product-handle'))) b.classList.add('is-voted');
    });
    document.querySelectorAll('[data-tb-file-label]').forEach(function (l) { l.setAttribute('data-default', l.textContent); });
    pinMine();
  }

  document.addEventListener('garage:changed', pinMine);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreState);
  else restoreState();
})();
